import {
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  where,
  getDocs,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { getDb } from './firebase';
import type { NotificationQueueItem } from '../types';

const PAGE_SIZE = 20;

export type NotificationStatusFilter = 'all' | 'sent' | 'pending';

export interface ListNotificationsResult {
  items: NotificationQueueItem[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

/**
 * List notification_queue items with optional status and userId filters.
 * Results are ordered by scheduledFor descending (newest first).
 */
export async function listNotifications(
  statusFilter: NotificationStatusFilter = 'all',
  userIdFilter = '',
  cursor: QueryDocumentSnapshot<DocumentData> | null = null
): Promise<ListNotificationsResult> {
  const colRef = collection(getDb(), 'notification_queue');
  const constraints = [];

  if (statusFilter === 'sent') {
    constraints.push(where('sent', '==', true));
  } else if (statusFilter === 'pending') {
    constraints.push(where('sent', '==', false));
  }

  if (userIdFilter.trim()) {
    constraints.push(where('userId', '==', userIdFilter.trim()));
  }

  constraints.push(orderBy('scheduledFor', 'desc'));
  constraints.push(limit(PAGE_SIZE + 1));

  if (cursor) {
    constraints.push(startAfter(cursor));
  }

  const q = query(colRef, ...constraints);
  const snap = await getDocs(q);

  const hasMore = snap.docs.length > PAGE_SIZE;
  const docs = hasMore ? snap.docs.slice(0, PAGE_SIZE) : snap.docs;
  const lastDoc = docs.length > 0 ? docs[docs.length - 1] : null;

  const items: NotificationQueueItem[] = docs.map(d => ({
    id: d.id,
    ...(d.data() as Omit<NotificationQueueItem, 'id'>),
  }));

  return { items, lastDoc, hasMore };
}
